import clsx from "clsx";
import { debounce, flatten, last } from "lodash-es";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TokenBadge, TokenIconSize } from "~/components";
import { RebateRecord } from "~/types/api/dex/rebate";
import { formatDateOnly } from "~/utils/format";
import { formatAmount, formatPrice } from "~/utils/number-format";
import useAsyncAction from "~/hook/useAsyncAction";
import LottieSticker from "~/components/LottieSticker";

type RebateListProps = {
    rebateList: (RebateRecord[] | null)[] | undefined;
    rebateListPageNum: number;
    setRebateListPageNum: (
        size: number
    ) => Promise<(RebateRecord[] | null)[] | undefined>;
};

const isNotNull = (item: RebateRecord | null): item is RebateRecord => {
    return item != null;
};

const RebateList: React.FC<RebateListProps> = ({
    rebateList = [],
    rebateListPageNum,
    setRebateListPageNum,
}) => {
    const { t } = useTranslation();
    const wholeRebateList = flatten(rebateList);
    const lastPage = last(rebateList);
    const loadMoreFn = useCallback(async () => {
        return setRebateListPageNum(rebateListPageNum + 1);
    }, [rebateListPageNum, setRebateListPageNum]);

    const loadMoreAction = useAsyncAction(loadMoreFn);

    useEffect(() => {
        const scrollableContainer = document.getElementById("tg-main-content");
        const onScroll = debounce(() => {
            const hasMore = lastPage && lastPage.length >= 0;
            const debouncedLoadMore = debounce(() => {
                loadMoreAction.run()
            }, 200);
            if (
                scrollableContainer &&
                scrollableContainer.scrollTop +
                    scrollableContainer.clientHeight >=
                    scrollableContainer.scrollHeight * 0.9 &&
                hasMore &&
                !loadMoreAction.isLoading
            ) {
                debouncedLoadMore();
            }
        }, 200);

        scrollableContainer?.addEventListener("scroll", onScroll);

        return () => {
            scrollableContainer?.removeEventListener("scroll", onScroll);
        };
    }, [lastPage, loadMoreAction]);

    const dataList = wholeRebateList.filter(isNotNull);

    if (dataList.length === 0) {
        return (
            <div className="py-10 px-3 flex flex-col items-center">
                <LottieSticker
                    lottieName="no-result"
                    className="w-[80px] h-[80px]"
                />
                <h3 className="mt-3 text-text_1 text-body1 font-semibold">
                    {t("no_reward_record")}
                </h3>
                <p className="mt-1 text-text_2 text-body3">
                    {t("no_transaction_yet")}
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-[12px] bg-bg_0 border-[0.5px] mt-2 border-border_2">
            <div className="px-[12px] h-[32px] flex items-end pb-1 justify-between text-direction1 font-Regular text-text_3">
                <div className="text-left w-[140px]">{t("token")}</div>
                <div className="text-right w-[128px]">{t("reward")}</div>
            </div>
            <div>
                {dataList.map((item, index) => {
                    const noBorder = index === 0;
                    return (
                        <div
                            className="flex items-stretch cursor-pointer"
                            key={index}
                        >
                            <div className="pl-3 flex-1 w-0 flex items-stretch">
                                <div className="flex-none py-3.5 mr-3">
                                    <TokenBadge
                                        size={TokenIconSize.small}
                                        tailIconSize={TokenIconSize.mini21}
                                        icon={item.logo}
                                        symbol={item.symbol}
                                        tokenIconBorder={true}
                                        chainIcon={item.chain_logo}
                                        chainName={item.chain_name}
                                    />
                                </div>
                                <div
                                    className={clsx(
                                        "flex-1 flex flex-col justify-center",
                                        {
                                            "border-t-[0.5px] border-border_2":
                                                !noBorder,
                                        }
                                    )}
                                >
                                    <div className="flex flex-col items-start">
                                        <div className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center ">
                                            <span
                                                className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[45vw]"
                                                dangerouslySetInnerHTML={{
                                                    __html: item.symbol.toUpperCase(),
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <span className="text-body4 text-text_2">
                                                {formatDateOnly(
                                                    String(item.ts)
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                key={index}
                                className={clsx(
                                    "flex-1 w-0 flex items-center justify-end pr-3",
                                    {
                                        "border-t-[0.5px] border-border_2":
                                            !noBorder,
                                    }
                                )}
                            >
                                <div className="flex flex-col items-end justify-center h-full">
                                    <span className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center">
                                        +{formatAmount(item.amount)}
                                    </span>

                                    <span className="text-body4 text-text_2">
                                        {formatPrice(item.value)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RebateList;
